import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLeadTracking } from '../hooks/useLeadTracking';
import { usePageMeta } from '../hooks/usePageMeta';
import { TCPADisclosure } from '../components/TCPADisclosure';
import { ChurchIcon, RouteIcon, DollarSignIcon, UsersIcon, MedalIcon, CheckCircle2Icon, ChevronDownIcon, ChevronLeftIcon, LoaderIcon, ShieldCheckIcon } from 'lucide-react';
import { useFormState } from '../hooks/useFormState';
import { formatPhone, stripPhone } from '../utils/phoneFormat';
import { submitPartnerLead } from '../services/partnerLead';
const ccuEducationLevels = [{
  label: 'High School Diploma',
  value: '1'
}, {
  label: 'No High School Diploma',
  value: '2'
}, {
  label: 'GED',
  value: '3'
}, {
  label: '0-12 College Credits',
  value: '4'
}, {
  label: 'More Than 12 College Credits',
  value: '5'
}, {
  label: 'Associates Degree',
  value: '6'
}, {
  label: 'Bachelors Degree',
  value: '7'
}];
const ccuStartTimeframes = [{
  label: 'Immediately',
  value: '1'
}, {
  label: '1-3 Months',
  value: '2'
}, {
  label: '4-6 Months',
  value: '3'
}, {
  label: '7-12 Months',
  value: '4'
}, {
  label: 'Later Than 1 Year',
  value: '5'
}];
function getGradYearOptions(): string[] {
  const years: string[] = [];
  for (let y = 2026; y >= 1980; y--) {
    years.push(String(y));
  }
  return years;
}
const gradYearOptions = getGradYearOptions();
const degreeLevels = [{
  label: 'Associate',
  value: 'associate'
}, {
  label: "Bachelor's",
  value: 'bachelors'
}, {
  label: "Master's",
  value: 'masters'
}];
interface CcuProgram {
  shortTitle: string;
  longTitle: string;
  degreeLevel: string;
  areaOfStudy: string;
  mProgram: string;
}
const ccuPrograms: CcuProgram[] = [
// Associate
{
  shortTitle: 'A.S. - Project Management',
  longTitle: 'Associate of Science (A.S.) in Project Management',
  degreeLevel: 'associate',
  areaOfStudy: 'business',
  mProgram: '1531'
},
// Bachelor's
{
  shortTitle: 'B.A. - Biblical Studies',
  longTitle: 'Bachelor of Arts (B.A.) in Biblical Studies',
  degreeLevel: 'bachelors',
  areaOfStudy: 'religious-studies',
  mProgram: '631'
}, {
  shortTitle: 'B.A. - Christian Ministry',
  longTitle: 'Bachelor of Arts (B.A.) in Christian Ministry',
  degreeLevel: 'bachelors',
  areaOfStudy: 'religious-studies',
  mProgram: '643'
}, {
  shortTitle: 'B.A. - Communication Studies',
  longTitle: 'Bachelor of Arts (B.A.) in Communication Studies',
  degreeLevel: 'bachelors',
  areaOfStudy: 'liberal-arts',
  mProgram: '649'
}, {
  shortTitle: 'B.A. - Creative Writing',
  longTitle: 'Bachelor of Arts (B.A.) in Creative Writing',
  degreeLevel: 'bachelors',
  areaOfStudy: 'liberal-arts',
  mProgram: '657'
}, {
  shortTitle: 'B.A. - Early Childhood Education (Licensure)',
  longTitle: 'Bachelor of Arts (B.A.) in Early Childhood Education (Licensure)',
  degreeLevel: 'bachelors',
  areaOfStudy: 'education',
  mProgram: '669'
}, {
  shortTitle: 'B.A. - Early Childhood Education (without Licensure)',
  longTitle: 'Bachelor of Arts (B.A.) in Early Childhood Education (without Licensure)',
  degreeLevel: 'bachelors',
  areaOfStudy: 'education',
  mProgram: '670'
}, {
  shortTitle: 'B.A. - Elementary Education (Licensure)',
  longTitle: 'Bachelor of Arts (B.A.) in Elementary Education (Licensure)',
  degreeLevel: 'bachelors',
  areaOfStudy: 'education',
  mProgram: '676'
}, {
  shortTitle: 'B.A. - Elementary Education (without Licensure)',
  longTitle: 'Bachelor of Arts (B.A.) in Elementary Education (without Licensure)',
  degreeLevel: 'bachelors',
  areaOfStudy: 'education',
  mProgram: '677'
}, {
  shortTitle: 'B.A. - Special Education, K-12 Generalist (Licensure)',
  longTitle: 'Bachelor of Arts (B.A.) in Special Education, K-12 Generalist (Licensure)',
  degreeLevel: 'bachelors',
  areaOfStudy: 'education',
  mProgram: '710'
}, {
  shortTitle: 'B.A. - Special Education, K-12 Generalist (without Licensure)',
  longTitle: 'Bachelor of Arts (B.A.) in Special Education, K-12 Generalist (without Licensure)',
  degreeLevel: 'bachelors',
  areaOfStudy: 'education',
  mProgram: '711'
}, {
  shortTitle: 'B.S. - Accountancy',
  longTitle: 'Bachelor of Science (B.S.) in Accountancy',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '620'
}, {
  shortTitle: 'B.S. - Applied Psychology',
  longTitle: 'Bachelor of Science (B.S.) in Applied Psychology',
  degreeLevel: 'bachelors',
  areaOfStudy: 'psychology',
  mProgram: '704'
}, {
  shortTitle: 'B.S. - Applied Psychology - Biblical Studies',
  longTitle: 'Bachelor of Science (B.S.) in Applied Psychology - Biblical Studies',
  degreeLevel: 'bachelors',
  areaOfStudy: 'psychology',
  mProgram: '705'
}, {
  shortTitle: 'B.S. - Applied Psychology - Clinical Counseling',
  longTitle: 'Bachelor of Science (B.S.) in Applied Psychology - Clinical Counseling',
  degreeLevel: 'bachelors',
  areaOfStudy: 'psychology',
  mProgram: '706'
}, {
  shortTitle: 'B.S. - Applied Psychology - Criminal Justice',
  longTitle: 'Bachelor of Science (B.S.) in Applied Psychology - Criminal Justice',
  degreeLevel: 'bachelors',
  areaOfStudy: 'psychology',
  mProgram: '707'
}, {
  shortTitle: 'B.S. - Business Administration',
  longTitle: 'Bachelor of Science (B.S.) in Business Administration',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '634'
}, {
  shortTitle: 'B.S. - Business - Entrepreneurship',
  longTitle: 'Bachelor of Science (B.S.) in Business - Entrepreneurship',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '635'
}, {
  shortTitle: 'B.S. - Business - International Business',
  longTitle: 'Bachelor of Science (B.S.) in Business - International Business',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '636'
}, {
  shortTitle: 'B.S. - Computer Information Technology',
  longTitle: 'Bachelor of Science (B.S.) in Computer Information Technology',
  degreeLevel: 'bachelors',
  areaOfStudy: 'information-technology',
  mProgram: '651'
}, {
  shortTitle: 'B.S. - Computer Information Technology - Cyber Security',
  longTitle: 'Bachelor of Science (B.S.) in Computer Information Technology - Cyber Security',
  degreeLevel: 'bachelors',
  areaOfStudy: 'information-technology',
  mProgram: '652'
}, {
  shortTitle: 'B.S. - Computer Information Technology - Database Management',
  longTitle: 'Bachelor of Science (B.S.) in Computer Information Technology - Database Management',
  degreeLevel: 'bachelors',
  areaOfStudy: 'information-technology',
  mProgram: '653'
}, {
  shortTitle: 'B.S. - Computer Information Technology, Data Engineering',
  longTitle: 'Bachelor of Science (B.S.) in Computer Information Technology, Data Engineering',
  degreeLevel: 'bachelors',
  areaOfStudy: 'information-technology',
  mProgram: '719'
}, {
  shortTitle: 'B.S. - Computer Information Technology - Networking',
  longTitle: 'Bachelor of Science (B.S.) in Computer Information Technology - Networking',
  degreeLevel: 'bachelors',
  areaOfStudy: 'information-technology',
  mProgram: '654'
}, {
  shortTitle: 'B.S. - Computer Science',
  longTitle: 'Bachelor of Science (B.S.) in Computer Science',
  degreeLevel: 'bachelors',
  areaOfStudy: 'information-technology',
  mProgram: '656'
}, {
  shortTitle: 'B.S. - Criminal Justice',
  longTitle: 'Bachelor of Science (B.S.) in Criminal Justice',
  degreeLevel: 'bachelors',
  areaOfStudy: 'criminal-justice',
  mProgram: '659'
}, {
  shortTitle: 'B.S. - Cyber Security',
  longTitle: 'Bachelor of Science (B.S.) in Cyber Security',
  degreeLevel: 'bachelors',
  areaOfStudy: 'information-technology',
  mProgram: '1767'
}, {
  shortTitle: 'B.S. - Data Analytics',
  longTitle: 'Bachelor of Science (B.S.) in Data Analytics',
  degreeLevel: 'bachelors',
  areaOfStudy: 'information-technology',
  mProgram: '668'
}, {
  shortTitle: 'B.S. - Economics',
  longTitle: 'Bachelor of Science (B.S.) in Economics',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '671'
}, {
  shortTitle: 'B.S. - Health Care Administration',
  longTitle: 'Bachelor of Science (B.S.) in Health Care Administration',
  degreeLevel: 'bachelors',
  areaOfStudy: 'health-care',
  mProgram: '680'
}, {
  shortTitle: 'B.S. - Human Resource Management',
  longTitle: 'Bachelor of Science (B.S.) in Human Resource Management',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '682'
}, {
  shortTitle: 'B.S. - Information Systems Management',
  longTitle: 'Bachelor of Science (B.S.) in Information Systems Management',
  degreeLevel: 'bachelors',
  areaOfStudy: 'information-technology',
  mProgram: '684'
}, {
  shortTitle: 'B.S. - Information Systems Management - Cyber Security',
  longTitle: 'Bachelor of Science (B.S.) in Information Systems Management - Cyber Security',
  degreeLevel: 'bachelors',
  areaOfStudy: 'information-technology',
  mProgram: '686'
}, {
  shortTitle: 'B.S. - Logistics and Supply Chain Management',
  longTitle: 'Bachelor of Science (B.S.) in Logistics and Supply Chain Management',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '688'
}, {
  shortTitle: 'B.S. - Marketing and Sales',
  longTitle: 'Bachelor of Science (B.S.) in Marketing and Sales',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '690'
}, {
  shortTitle: 'B.S. - Nonprofit Management',
  longTitle: 'Bachelor of Science (B.S.) in Nonprofit Management',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '692'
}, {
  shortTitle: 'B.S. - Organizational Management in Project Management',
  longTitle: 'Bachelor of Science (B.S.) in Organizational Management in Project Management',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '699'
},
// Master's
{
  shortTitle: 'M.A. - Biblical Studies',
  longTitle: 'Master of Arts (M.A.) in Biblical Studies',
  degreeLevel: 'masters',
  areaOfStudy: 'religious-studies',
  mProgram: '632'
}, {
  shortTitle: 'M.A. - Clinical Mental Health Counseling - Marriage & Family Therapy',
  longTitle: 'Master of Arts (M.A.) in Clinical Mental Health Counseling - Marriage & Family Therapy',
  degreeLevel: 'masters',
  areaOfStudy: 'psychology',
  mProgram: '646'
}, {
  shortTitle: 'M.A. - Clinical Mental Health Counseling - Online Curriculum',
  longTitle: 'Master of Arts (M.A.) in Clinical Mental Health Counseling - Online Curriculum',
  degreeLevel: 'masters',
  areaOfStudy: 'psychology',
  mProgram: '645'
}, {
  shortTitle: 'M.A. - Clinical Mental Health Counseling - On Site Curriculum',
  longTitle: 'Master of Arts (M.A.) in Clinical Mental Health Counseling - On Site Curriculum',
  degreeLevel: 'masters',
  areaOfStudy: 'psychology',
  mProgram: '644'
}, {
  shortTitle: 'M.A. - Clinical Mental Health Counseling - Substance Use Disorders',
  longTitle: 'Master of Arts (M.A.) in Clinical Mental Health Counseling - Substance Use Disorders',
  degreeLevel: 'masters',
  areaOfStudy: 'psychology',
  mProgram: '647'
}, {
  shortTitle: 'M.A. - Counseling Dual - Mental Health & School',
  longTitle: 'Master of Arts (M.A.) in Counseling Dual - Mental Health & School',
  degreeLevel: 'masters',
  areaOfStudy: 'psychology',
  mProgram: '723'
}, {
  shortTitle: 'M.A. - Curriculum and Instruction',
  longTitle: 'Master of Arts (M.A.) in Curriculum and Instruction',
  degreeLevel: 'masters',
  areaOfStudy: 'education',
  mProgram: '665'
}, {
  shortTitle: 'M.A. - Curriculum and Instruction - Alternative Licensing',
  longTitle: 'Master of Arts (M.A.) in Curriculum and Instruction - Alternative Licensing',
  degreeLevel: 'masters',
  areaOfStudy: 'education',
  mProgram: '662'
}, {
  shortTitle: 'M.A. - Curriculum and Instruction - Biblical Studies',
  longTitle: 'Master of Arts (M.A.) in Curriculum and Instruction - Biblical Studies',
  degreeLevel: 'masters',
  areaOfStudy: 'education',
  mProgram: '663'
}, {
  shortTitle: 'M.A. - Curriculum and Instruction - Culturally and Linguistically Diverse',
  longTitle: 'Master of Arts (M.A.) in Curriculum and Instruction - Culturally and Linguistically Diverse',
  degreeLevel: 'masters',
  areaOfStudy: 'education',
  mProgram: '664'
}, {
  shortTitle: 'M.A. - Executive Leadership in Christian Ministry',
  longTitle: 'Master of Arts (M.A.) in Executive Leadership in Christian Ministry',
  degreeLevel: 'masters',
  areaOfStudy: 'religious-studies',
  mProgram: '678'
}, {
  shortTitle: 'M.A. - School Counseling',
  longTitle: 'Master of Arts (M.A.) in School Counseling',
  degreeLevel: 'masters',
  areaOfStudy: 'education',
  mProgram: '709'
}, {
  shortTitle: 'M.A. - Theological Studies',
  longTitle: 'Master of Arts (M.A.) in Theological Studies',
  degreeLevel: 'masters',
  areaOfStudy: 'religious-studies',
  mProgram: '716'
}, {
  shortTitle: 'M.B.A. - Business Administration - Advanced Accounting',
  longTitle: 'Master of Business Administration (M.B.A.) in Business Administration - Advanced Accounting',
  degreeLevel: 'masters',
  areaOfStudy: 'business',
  mProgram: '637'
}, {
  shortTitle: 'M.B.A. - Business Administration - Cyber Security',
  longTitle: 'Master of Business Administration (M.B.A.) in Business Administration - Cyber Security',
  degreeLevel: 'masters',
  areaOfStudy: 'business',
  mProgram: '638'
}, {
  shortTitle: 'M.B.A. - Business Administration, Economics',
  longTitle: 'Master of Business Administration (M.B.A.) in Business Administration, Economics',
  degreeLevel: 'masters',
  areaOfStudy: 'business',
  mProgram: '718'
}, {
  shortTitle: 'M.B.A. - Business Administration - Enterprise Agility',
  longTitle: 'Master of Business Administration (M.B.A.) in Business Administration - Enterprise Agility',
  degreeLevel: 'masters',
  areaOfStudy: 'business',
  mProgram: '642'
}, {
  shortTitle: 'M.B.A. - Business Administration - Health Care Administration',
  longTitle: 'Master of Business Administration (M.B.A.) in Business Administration - Health Care Administration',
  degreeLevel: 'masters',
  areaOfStudy: 'business',
  mProgram: '639'
}, {
  shortTitle: 'M.B.A. - Business Administration - Leadership',
  longTitle: 'Master of Business Administration (M.B.A.) in Business Administration - Leadership',
  degreeLevel: 'masters',
  areaOfStudy: 'business',
  mProgram: '640'
}, {
  shortTitle: 'M.B.A. - Business Administration - Project Management',
  longTitle: 'Master of Business Administration (M.B.A.) in Business Administration - Project Management',
  degreeLevel: 'masters',
  areaOfStudy: 'business',
  mProgram: '641'
}, {
  shortTitle: 'M.Ed. - Educational Leadership (Principal Licensure)',
  longTitle: 'Master of Education (M.Ed.) in Educational Leadership (Principal Licensure)',
  degreeLevel: 'masters',
  areaOfStudy: 'education',
  mProgram: '673'
}, {
  shortTitle: 'M.Ed. - Educational Leadership (without Licensure)',
  longTitle: 'Master of Education (M.Ed.) in Educational Leadership (without Licensure)',
  degreeLevel: 'masters',
  areaOfStudy: 'education',
  mProgram: '674'
}, {
  shortTitle: 'M.Ed. - Education - Teaching English to Speakers of Other Languages',
  longTitle: 'Master of Education (M.Ed.) in Education - Teaching English to Speakers of Other Languages',
  degreeLevel: 'masters',
  areaOfStudy: 'education',
  mProgram: '675'
}, {
  shortTitle: 'M.Ed. - Special Education - Emphasis in Alternative Licensing',
  longTitle: 'Master of Education (M.Ed.) in Special Education - Emphasis in Alternative Licensing',
  degreeLevel: 'masters',
  areaOfStudy: 'education',
  mProgram: '713'
}, {
  shortTitle: 'M.Ed. - Special Education - Endorsement in Special Education Generalist',
  longTitle: 'Master of Education (M.Ed.) in Special Education - Endorsement in Special Education Generalist',
  degreeLevel: 'masters',
  areaOfStudy: 'education',
  mProgram: '714'
}, {
  shortTitle: 'M.Ed. - Special Education (Licensure)',
  longTitle: 'Master of Education (M.Ed.) in Special Education (Licensure)',
  degreeLevel: 'masters',
  areaOfStudy: 'education',
  mProgram: '715'
}, {
  shortTitle: 'M.Ed. - Special Education (without Licensure)',
  longTitle: 'Master of Education (M.Ed.) in Special Education (without Licensure)',
  degreeLevel: 'masters',
  areaOfStudy: 'education',
  mProgram: '712'
}, {
  shortTitle: 'M.S. - Accounting',
  longTitle: 'Master of Science (M.S.) in Accounting',
  degreeLevel: 'masters',
  areaOfStudy: 'business',
  mProgram: '621'
}, {
  shortTitle: 'M.S. - Criminal Justice',
  longTitle: 'Master of Science (M.S.) in Criminal Justice',
  degreeLevel: 'masters',
  areaOfStudy: 'criminal-justice',
  mProgram: '660'
}, {
  shortTitle: 'M.S. - Criminal Justice - Campus, Event, and Organizational Safety',
  longTitle: 'Master of Science (M.S.) in Criminal Justice - Campus, Event, and Organizational Safety',
  degreeLevel: 'masters',
  areaOfStudy: 'criminal-justice',
  mProgram: '661'
}, {
  shortTitle: 'M.S. - Cyber Security',
  longTitle: 'Master of Science (M.S.) in Cyber Security',
  degreeLevel: 'masters',
  areaOfStudy: 'information-technology',
  mProgram: '666'
}, {
  shortTitle: 'M.S. - Human Resource Management',
  longTitle: 'Master of Science (M.S.) in Human Resource Management',
  degreeLevel: 'masters',
  areaOfStudy: 'business',
  mProgram: '683'
}, {
  shortTitle: 'M.S. - Master in Organizational Leadership',
  longTitle: 'Master of Science (M.S.) in Master in Organizational Leadership',
  degreeLevel: 'masters',
  areaOfStudy: 'business',
  mProgram: '701'
}, {
  shortTitle: 'M.S. - Psychology',
  longTitle: 'Master of Science (M.S.) in Psychology',
  degreeLevel: 'masters',
  areaOfStudy: 'psychology',
  mProgram: '703'
}];
export function CCULandingPage() {
  usePageMeta('Colorado Christian University | Online Degree Programs', 'Explore flexible online degree programs at Colorado Christian University. Accredited Christ-centered programs designed for working adults.', {
    ogImage: 'https://cdn.magicpatterns.com/uploads/3d3gnGrvtCroZLDPHFprwK/CCU_1.webp',
    ogType: 'website'
  });
  const navigate = useNavigate();
  const formRef = useRef<HTMLDivElement>(null);
  const [selectedDegreeLevel, setSelectedDegreeLevel] = useState('');
  const [selectedAreaOfStudy, setSelectedAreaOfStudy] = useState('');
  const [selectedProgram, setSelectedProgram] = useState('');
  const [formStep, setFormStep] = useState(0);
  const [zipLoading, setZipLoading] = useState(false);
  const [zipLookedUp, setZipLookedUp] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [addressError, setAddressError] = useState('');
  const [userIP, setUserIP] = useState('');
  const [urlParams] = useState<Record<string, string>>(() => {
    const params: Record<string, string> = {};
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.forEach((value, key) => {
      params[key] = value;
    });
    return params;
  });
  const {
    leadIdToken,
    trustedFormUrl
  } = useLeadTracking();
  useEffect(() => {
    fetch('https://api.ipify.org?format=json').then(res => res.json()).then(data => setUserIP(data.ip || '')).catch(() => setUserIP(''));
  }, []);
  const {
    formData,
    errors,
    updateFormData,
    updateMultiple,
    validateUniStep1,
    validateUniStep2,
    validateUniStep3
  } = useFormState();
  const lookupZip = useCallback(async (zip: string) => {
    if (zip.length < 5) return;
    setZipLoading(true);
    try {
      const res = await fetch(`https://api.zippopotam.us/us/${zip}`);
      if (res.ok) {
        const data = await res.json();
        const place = data.places?.[0];
        if (place) {
          updateMultiple({
            city: place['place name'] || '',
            state: place['state abbreviation'] || ''
          });
          setZipLookedUp(true);
        }
      } else {
        setZipLookedUp(true);
      }
    } catch {
      setZipLookedUp(true);
    } finally {
      setZipLoading(false);
    }
  }, [updateMultiple]);
  const areaLabels: Record<string, string> = {
    business: 'Business',
    'criminal-justice': 'Criminal Justice',
    education: 'Education',
    'health-care': 'Health Care',
    'information-technology': 'Information Technology',
    'liberal-arts': 'Liberal Arts',
    psychology: 'Psychology & Counseling',
    'religious-studies': 'Religious Studies & Ministry'
  };
  const allAreaSlugs = Object.keys(areaLabels);
  const getAvailableAreas = () => {
    if (!selectedDegreeLevel || selectedDegreeLevel === 'undecided') {
      return allAreaSlugs;
    }
    const areasWithPrograms = new Set<string>();
    ccuPrograms.forEach(prog => {
      if (prog.degreeLevel === selectedDegreeLevel) {
        areasWithPrograms.add(prog.areaOfStudy);
      }
    });
    return allAreaSlugs.filter(slug => areasWithPrograms.has(slug));
  };
  const getAvailablePrograms = () => {
    if (!selectedAreaOfStudy) return [];
    return ccuPrograms.filter(prog => {
      const areaMatch = prog.areaOfStudy === selectedAreaOfStudy;
      const degreeMatch = !selectedDegreeLevel || prog.degreeLevel === selectedDegreeLevel;
      return areaMatch && degreeMatch;
    });
  };
  const handleDegreeLevelChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newLevel = e.target.value;
    setSelectedDegreeLevel(newLevel);
    setSelectedAreaOfStudy('');
    setSelectedProgram('');
  };
  const handleAreaOfStudyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newArea = e.target.value;
    setSelectedAreaOfStudy(newArea);
    setSelectedProgram('');
  };
  const handleProgramChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newProgram = e.target.value;
    setSelectedProgram(newProgram);
    if (newProgram && formStep === 0) {
      setFormStep(1);
    }
  };
  const handleStep1Next = () => {
    if (validateUniStep1()) setFormStep(2);
  };
  const handleStep2Next = () => {
    const zipValid = validateUniStep2();
    const addrValid = formData.address.trim().length > 0;
    if (!addrValid) {
      setAddressError('Street address is required.');
    } else {
      setAddressError('');
    }
    if (zipValid && addrValid) {
      lookupZip(formData.zipCode);
      setFormStep(3);
    }
  };
  const handleStep3Submit = async () => {
    if (validateUniStep3()) {
      setIsSubmitting(true);
      const currentProgram = ccuPrograms.find(p => p.shortTitle === selectedProgram);
      try {
        const params = new URLSearchParams({
          ...urlParams,
          mProgram: currentProgram?.mProgram || '',
          programTitle: currentProgram?.longTitle || '',
          degreeLevel: selectedDegreeLevel,
          areaOfStudy: selectedAreaOfStudy,
          highestEducation: formData.currentEducation,
          gradYear: formData.gradYear,
          startTimeframe: formData.startDate,
          zipCode: formData.zipCode,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: stripPhone(formData.phone),
          leadIdToken: leadIdToken,
          trustedFormUrl: trustedFormUrl,
          webLandingURL: window.location.href,
          userIP: userIP
        });
        await submitPartnerLead('ccu', params);
      } catch (err) {
        console.error('Partner lead submission error:', err);
      } finally {
        setIsSubmitting(false);
      }
      navigate('/thank-you', {
        state: {
          formData,
          partner: {
            name: 'Colorado Christian University',
            logo: 'https://cdn.magicpatterns.com/uploads/3d3gnGrvtCroZLDPHFprwK/CCU_1.webp',
            primaryColor: '#0F3057',
            accentColor: '#D4A943',
            backUrl: '/colleges/colorado-christian-university',
            programTitle: currentProgram?.longTitle || ''
          }
        }
      });
    }
  };
  const scrollToForm = () => {
    formRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const totalSteps = 3;
  const progressPercent = formStep === 0 ? 0 : Math.round(formStep / totalSteps * 100);
  const selectClasses = (hasError: boolean) => `block w-full px-4 py-3 text-sm border rounded-md bg-white appearance-none cursor-pointer ${hasError ? 'border-red-400' : 'border-gray-300 focus:ring-[#D4A943] focus:border-[#D4A943]'}`;
  const inputClasses = (hasError: boolean) => `block w-full px-4 py-3 text-sm border rounded-md shadow-sm ${hasError ? 'border-red-400' : 'border-gray-300 focus:ring-[#D4A943] focus:border-[#D4A943]'}`;
  const availableAreas = getAvailableAreas();
  const availablePrograms = getAvailablePrograms();
  const renderFormContent = () => {
    if (formStep === 0) {
      return <div className="space-y-4" data-id="element-1146" data-mp="3p25j43bsiols">
          <div data-id="element-1147" data-mp="3p36yul3r3z9u">
            <label htmlFor="degree-level-select" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1148" data-mp="3p2nrfybq8ta2">
              Degree Level
            </label>
            <div className="relative" data-id="element-1149" data-mp="3aghzqkdevo3z">
              <select id="degree-level-select" value={selectedDegreeLevel} onChange={handleDegreeLevelChange} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md bg-gray-50 appearance-none cursor-pointer focus:ring-[#D4A943] focus:border-[#D4A943] font-medium text-gray-800" data-id="element-1150" data-mp="3ahjb6pycacm8">
                <option value="" data-id="element-1151" data-mp="3fgmhsetywqwb">-- Select Degree Level --</option>
                {degreeLevels.map(level => <option key={level.value} value={level.value} data-id="element-1152" data-mp="3tcgdtx9n7ls0">
                    {level.label}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500" data-id="element-1153" data-mp="6z8565xobofa">
                <ChevronDownIcon className="h-4 w-4" data-id="element-1154" data-mp="20oks9yqakp1" />
              </div>
            </div>
          </div>

          <div data-id="element-1155" data-mp="g06cax3esc23">
            <label htmlFor="area-of-study-select" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1156" data-mp="fzo7vd6lerj3">
              Area of Study
            </label>
            <div className="relative" data-id="element-1157" data-mp="34tkzb9alrws2">
              <select id="area-of-study-select" value={selectedAreaOfStudy} onChange={handleAreaOfStudyChange} disabled={!selectedDegreeLevel} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md bg-gray-50 appearance-none cursor-pointer focus:ring-[#D4A943] focus:border-[#D4A943] font-medium text-gray-800 disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400" data-id="element-1158" data-mp="34qgmxomzhuqb">
                <option value="" data-id="element-1159" data-mp="3nl5i5jkxoqsa">
                  {selectedDegreeLevel ? '-- Select Area of Study --' : 'Select degree level first'}
                </option>
                {availableAreas.map(slug => <option key={slug} value={slug} data-id="element-1160" data-mp="19gozgmsr0ga">
                    {areaLabels[slug]}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500" data-id="element-1161" data-mp="233fz3yo6nbre">
                <ChevronDownIcon className="h-4 w-4" data-id="element-1162" data-mp="275kmifo1fc5l" />
              </div>
            </div>
          </div>

          <div data-id="element-1163" data-mp="1zls5byjli5g8">
            <label htmlFor="program-select" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1164" data-mp="1zmbcqlbmdbg0">
              Program
            </label>
            <div className="relative" data-id="element-1165" data-mp="1pw8n8v3pifmf">
              <select id="program-select" value={selectedProgram} onChange={handleProgramChange} disabled={!selectedAreaOfStudy} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md bg-gray-50 appearance-none cursor-pointer focus:ring-[#D4A943] focus:border-[#D4A943] font-medium text-gray-800 disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400" data-id="element-1166" data-mp="1pt4avag38dko">
                <option value="" data-id="element-1167" data-mp="29hd3tmpggnl8">
                  {!selectedAreaOfStudy ? 'Select area of study first' : '-- Select Program --'}
                </option>
                {availablePrograms.map(prog => <option key={prog.shortTitle} value={prog.shortTitle} data-id="element-1168" data-mp="y5m2bnm94s7h">
                    {prog.shortTitle}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500" data-id="element-1169" data-mp="2t6o5beevbzkt">
                <ChevronDownIcon className="h-4 w-4" data-id="element-1170" data-mp="2x9u6w8r1d2ky" />
              </div>
            </div>
          </div>

          <p className="text-xs text-gray-500 text-center pt-2" data-id="element-1171" data-mp="2ja1hqhv5quf3">
            Select all three options above to continue
          </p>
        </div>;
    }
    if (formStep === 1) {
      return <div className="space-y-4" data-id="element-1172" data-mp="3bxfae3rtp3te">
          <div data-id="element-1173" data-mp="3bygwd3adnapw">
            <label htmlFor="step1-program" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1174" data-mp="3c01flwr8r78g">
              Selected Program
            </label>
            <div className="relative" data-id="element-1175" data-mp="327ch02gshnuj">
              <select id="step1-program" value={selectedProgram} onChange={e => setSelectedProgram(e.target.value)} className="block w-full px-4 py-3 text-sm border border-gray-200 rounded-md bg-gray-50 appearance-none cursor-pointer font-medium text-gray-700" data-id="element-1176" data-mp="376yt28kvsy1r">
                {availablePrograms.map(prog => <option key={prog.shortTitle} value={prog.shortTitle} data-id="element-1177" data-mp="1o8aqznr78tne">
                    {prog.shortTitle}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-400" data-id="element-1178" data-mp="1j8obt8rt93by">
                <ChevronDownIcon className="h-4 w-4" data-id="element-1179" data-mp="1x2p2rgcitm1r" />
              </div>
            </div>
          </div>

          <div className="border-t border-gray-100 pt-3" data-id="element-1180" data-mp="1t1daf329impv" />

          <div data-id="element-1181" data-mp="15a7vqr8260h5">
            <label htmlFor="currentEducation" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1182" data-mp="159ycrnlfktky">
              Highest Level of Education
            </label>
            <select id="currentEducation" value={formData.currentEducation} onChange={e => updateFormData('currentEducation', e.target.value)} className={selectClasses(!!errors.currentEducation)} data-id="element-1183" data-mp="1eawd9vwnuiys">
              <option value="" data-id="element-1184" data-mp="2pnyoscjwkic3">-- Please Select --</option>
              {ccuEducationLevels.map(level => <option key={level.value} value={level.value} data-id="element-1185" data-mp="2kpdpcfch62op">
                  {level.label}
                </option>)}
            </select>
            {errors.currentEducation && <p className="mt-1 text-xs text-red-600" data-id="element-1186" data-mp="2vbemzkcy77hg">
                {errors.currentEducation}
              </p>}
          </div>

          <div data-id="element-1187" data-mp="2qcux58v8hbjt">
            <label htmlFor="startDate" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1188" data-mp="2qcle658lw4nm">
              When Would You Like to Start?
            </label>
            <select id="startDate" value={formData.startDate} onChange={e => updateFormData('startDate', e.target.value)} className={selectClasses(!!errors.startDate)} data-id="element-1189" data-mp="3izfowafmfpnw">
              <option value="" data-id="element-1190" data-mp="39ae6yepimy7a">-- Please Select --</option>
              {ccuStartTimeframes.map(opt => <option key={opt.value} value={opt.value} data-id="element-1191" data-mp="34c4am0s21rk3">
                  {opt.label}
                </option>)}
            </select>
            {errors.startDate && <p className="mt-1 text-xs text-red-600" data-id="element-1192" data-mp="5n5iavuj5pr4">{errors.startDate}</p>}
          </div>

          <div data-id="element-1193" data-mp="tji435jzec7">
            <label htmlFor="gradYear" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1194" data-mp="t1akadfxdoz">
              High School Graduation Year
            </label>
            <select id="gradYear" value={formData.gradYear} onChange={e => updateFormData('gradYear', e.target.value)} className={selectClasses(!!errors.gradYear)} data-id="element-1195" data-mp="d5vka2b0iige">
              <option value="" data-id="element-1196" data-mp="n3t8ipsvgf90">-- Please Select --</option>
              {gradYearOptions.map(yr => <option key={yr} value={yr} data-id="element-1197" data-mp="r5ek87to6nhm">
                  {yr}
                </option>)}
            </select>
            {errors.gradYear && <p className="mt-1 text-xs text-red-600" data-id="element-1198" data-mp="w2wsrx3rz0p3">{errors.gradYear}</p>}
          </div>

          <div className="flex gap-3 pt-2" data-id="element-1199" data-mp="10vzexk6837v9">
            <button type="button" onClick={() => setFormStep(0)} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-1200" data-mp="10xafzc8aa1io">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-1201" data-mp="1eu7e7yqqel9l" />
              Back
            </button>
            <button type="button" onClick={handleStep1Next} className="flex-1 bg-gradient-to-r from-[#D4A943] to-[#B8902F] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200" data-id="element-1202" data-mp="3butub3i2wbcn">
              Next
            </button>
          </div>
        </div>;
    }
    if (formStep === 2) {
      return <div className="space-y-4" data-id="element-1203" data-mp="3fuu76jpj4ehl">
          <div data-id="element-1204" data-mp="3ull240mf3tox">
            <label htmlFor="zipCode" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1205" data-mp="3ul1rl4z3k7kx">
              Zip Code
            </label>
            <input type="text" id="zipCode" value={formData.zipCode} onChange={e => {
            const val = e.target.value.replace(/\D/g, '').slice(0, 5);
            updateFormData('zipCode', val);
            setZipLookedUp(false);
            if (val.length === 5) lookupZip(val);
          }} className={inputClasses(!!errors.zipCode)} placeholder="12345" maxLength={5} data-id="element-1206" data-mp="3pp4dz6f7xfbu" />
            {errors.zipCode && <p className="mt-1 text-xs text-red-600" data-id="element-1207" data-mp="2599eowi05mdg">{errors.zipCode}</p>}
            {zipLoading && <div className="mt-2 flex items-center text-xs text-gray-500" data-id="element-1208" data-mp="1v52p7rthcefv">
                <LoaderIcon className="w-3 h-3 mr-1 animate-spin" data-id="element-1209" data-mp="1z41uxen1cf8d" />
                Looking up your location...
              </div>}
          </div>

          {zipLookedUp && <>
              <div className="grid grid-cols-2 gap-3" data-id="element-1210" data-mp="1qb80g9z0p99x">
                <div data-id="element-1211" data-mp="1bez0t7b7stwz">
                  <label htmlFor="city" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1212" data-mp="1beh82knrgxtt">
                    City
                  </label>
                  <input type="text" id="city" value={formData.city} onChange={e => updateFormData('city', e.target.value)} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-[#D4A943] focus:border-[#D4A943] bg-gray-50" placeholder="City" data-id="element-1213" data-mp="12eamebhwsrjk" />
                </div>
                <div data-id="element-1214" data-mp="31retepoqk0vu">
                  <label htmlFor="state" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1215" data-mp="31rohu8u4tzzf">
                    State
                  </label>
                  <input type="text" id="state" value={formData.state} onChange={e => updateFormData('state', e.target.value)} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-[#D4A943] focus:border-[#D4A943] bg-gray-50" placeholder="State" data-id="element-1216" data-mp="wq2intsuseaq" />
                </div>
              </div>
              <div data-id="element-1217" data-mp="ircoon1byuql">
                <label htmlFor="address" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1218" data-mp="irvw39tcu0qd">
                  Street Address
                </label>
                <input type="text" id="address" value={formData.address} onChange={e => {
              updateFormData('address', e.target.value);
              if (addressError) setAddressError('');
            }} className={`block w-full px-4 py-3 text-sm border rounded-md shadow-sm ${addressError ? 'border-red-400' : 'border-gray-300 focus:ring-[#D4A943] focus:border-[#D4A943]'}`} placeholder="123 Main St" data-id="element-1219" data-mp="91je9pcxclnx" />
                {addressError && <p className="mt-1 text-xs text-red-600" data-id="element-1220" data-mp="26nsm6buv93dw">{addressError}</p>}
              </div>
            </>}

          <div className="flex gap-3 pt-2" data-id="element-1221" data-mp="2bp9irv6bi3oo">
            <button type="button" onClick={() => setFormStep(1)} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-1222" data-mp="2foidb296m5la">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-1223" data-mp="1nz5edllb1zwm" />
              Back
            </button>
            <button type="button" onClick={handleStep2Next} className="flex-1 bg-gradient-to-r from-[#D4A943] to-[#B8902F] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200" data-id="element-1224" data-mp="1rzpbip5vp3ir">
              Next
            </button>
          </div>
        </div>;
    }
    if (formStep === 3) {
      return <div className="space-y-4" data-id="element-1225" data-mp="1986bmr75i5ky">
          <div className="grid grid-cols-2 gap-3" data-id="element-1226" data-mp="197db47ysccb9">
            <div data-id="element-1227" data-mp="2cjc1cmoqjku0">
              <label htmlFor="firstName" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1228" data-mp="2cisjt72zyygi">
                First Name
              </label>
              <input type="text" id="firstName" value={formData.firstName} onChange={e => updateFormData('firstName', e.target.value)} className={inputClasses(!!errors.firstName)} placeholder="Jane" data-id="element-1229" data-mp="2lbw3hx48k7jl" />
              {errors.firstName && <p className="mt-1 text-xs text-red-600" data-id="element-1230" data-mp="1tk7vfmshq76k">{errors.firstName}</p>}
            </div>
            <div data-id="element-1231" data-mp="1om6cd1v2e7v3">
              <label htmlFor="lastName" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1232" data-mp="1omoj4sfcs4h9">
                Last Name
              </label>
              <input type="text" id="lastName" value={formData.lastName} onChange={e => updateFormData('lastName', e.target.value)} className={inputClasses(!!errors.lastName)} placeholder="Doe" data-id="element-1233" data-mp="1xml0e8zey12a" />
              {errors.lastName && <p className="mt-1 text-xs text-red-600" data-id="element-1234" data-mp="3929nd0co3033">{errors.lastName}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3" data-id="element-1235" data-mp="3hhfoimfmidk5">
            <div data-id="element-1236" data-mp="3hkv3zqd7lom3">
              <label htmlFor="email" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1237" data-mp="3lju8j9v13jd0">
                Email Address
              </label>
              <input type="email" id="email" value={formData.email} onChange={e => updateFormData('email', e.target.value)} className={inputClasses(!!errors.email)} placeholder="jane@example.com" data-id="element-1238" data-mp="37mff7u1nmrfr" />
              {errors.email && <p className="mt-1 text-xs text-red-600" data-id="element-1239" data-mp="92p918cbwkwq">{errors.email}</p>}
            </div>
            <div data-id="element-1240" data-mp="94i6py4yrusl">
              <label htmlFor="phone" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1241" data-mp="3qjgj1bjh2li4">
                Phone Number
              </label>
              <input type="tel" id="phone" value={formData.phone} onChange={e => updateFormData('phone', formatPhone(e.target.value))} className={inputClasses(!!errors.phone)} placeholder="(555)123-4567" maxLength={13} data-id="element-1242" data-mp="3vg6zhzz6vj42" />
              {errors.phone && <p className="mt-1 text-xs text-red-600" data-id="element-1243" data-mp="2nwux35d36kn5">{errors.phone}</p>}
            </div>
          </div>

          <TCPADisclosure checked={formData.tcpaConsent} onChange={checked => updateFormData('tcpaConsent', checked)} error={errors.tcpaConsent} checkboxClassName="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#D4A943] focus:ring-[#D4A943] cursor-pointer flex-shrink-0" data-id="element-1244" data-mp="2j5cuydzbue0l">
            By submitting this form, I agree to be called or texted at the
            number listed above by or on behalf of Colorado Christian University
            (CCU) using an automatic or AI telephone system. All communication
            will be informational and commercial regarding educational services.
            Consent is not required as a condition of enrollment. CCU may also
            contact me via email.
          </TCPADisclosure>

          <div className="flex gap-3 pt-2" data-id="element-1245" data-mp="1mg3x3chm1738">
            <button type="button" onClick={() => setFormStep(2)} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-1246" data-mp="1hg94n4i6s9g2">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-1247" data-mp="1ycuaw649xdg" />
              Back
            </button>
            <button type="button" onClick={handleStep3Submit} disabled={isSubmitting} className="flex-1 bg-gradient-to-r from-[#D4A943] to-[#B8902F] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-1248" data-mp="5yn28edgyxuy">
              {isSubmitting && <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-1249" data-mp="pn5ksd3yzd0o" />}
              {isSubmitting ? 'Submitting...' : 'Request Info'}
            </button>
          </div>
        </div>;
    }
    return null;
  };
  return <div className="min-h-screen bg-white font-sans text-gray-800" data-id="element-1250" data-mp="2reqxifn7e012">
      {/* Header */}
      <header className="bg-white py-3 px-6 sm:px-12 flex items-center justify-center shadow-sm" data-id="element-1251" data-mp="2vfklta4k5guh">
        <img src="https://cdn.magicpatterns.com/uploads/3d3gnGrvtCroZLDPHFprwK/CCU_1.webp" alt="Colorado Christian University Logo" className="h-14 sm:h-16 w-auto object-contain" data-id="element-1252" data-mp="36mzlf8t2q7bi" />
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#0F3057] to-[#1A4A7A] relative overflow-hidden" data-id="element-1253" data-mp="3fn4fkh99b97q">
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none bg-no-repeat bg-cover bg-center mix-blend-soft-light opacity-70" style={{
        backgroundImage: "url('https://cdn.magicpatterns.com/uploads/8uFwSQxxc9xYA37moQruEX/ChatGPT_Image_May_6,_2026,_08_20_39_PM.png')"
      }} data-id="element-1254" data-mp="3klfsx1yrg1up" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 flex flex-col lg:flex-row items-center gap-10 lg:gap-16 relative z-10" data-id="element-1255" data-mp="2wvv78bb82qhq">
          {/* Left Content */}
          <div className="flex-1 text-white max-w-xl" data-id="element-1256" data-mp="248r0a0skk23b">
            <p className="text-white font-medium mb-3 text-lg tracking-wide" data-id="element-1257" data-mp="2464qrvm4bcig">
              Colorado Christian University
            </p>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-[1.05] mb-6 text-white tracking-tight" data-id="element-1258" data-mp="21ocinz00d2ai">
              {selectedProgram && selectedProgram !== 'undecided' ? (() => {
              const currentProg = ccuPrograms.find(p => p.shortTitle === selectedProgram);
              return currentProg?.longTitle || selectedProgram;
            })() : <>
                  Christ-Centered
                  <br data-id="element-1259" data-mp="1iq07y8tg7kct" />
                  Online Degree Programs
                </>}
            </h1>
            <p className="text-lg text-white/85 max-w-lg leading-relaxed" data-id="element-1260" data-mp="1dxyq87dih1fe">
              Colorado Christian University offers accredited online degree programs taught with a Christian worldview. With a flexible online format, dedicated support staff, and affordable tuition, CCU Online equips you for success to make a positive impact in the world.
            </p>
          </div>

          {/* Right Form */}
          <div ref={formRef} className="w-full lg:w-[520px] bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.3)] p-8 sm:p-10 flex-shrink-0 relative z-10" data-id="element-1261" data-mp="v0np0h8bjxxb">
            <form onSubmit={e => e.preventDefault()} id="ccu-lead-form" data-id="element-1262" data-mp="pd3pzr96vo5q">
              {/* Hidden inputs for tracking */}
              <input type="hidden" name="xxTrustedFormCertUrl" id="xxTrustedFormCertUrl" value="" data-id="element-1263" data-mp="becgki59uqnx" />
              <input type="hidden" name="leadid_token" id="leadid_token" value="" data-id="element-1264" data-mp="gbvu1fu5azet" />

              <h2 className="text-2xl sm:text-3xl font-bold text-[#0F3057] mb-4" data-id="element-1265" data-mp="141q45pn2y9vd">
                Take the next step in your education.
              </h2>

              {/* Progress Bar */}
              <div className="w-full bg-gray-100 rounded-full h-2 mb-8 overflow-hidden" data-id="element-1266" data-mp="ucomlv0vzkfa">
                <div className="bg-gradient-to-r from-[#D4A943] to-[#B8902F] h-2 rounded-full transition-all duration-500 ease-out" style={{
                width: `${progressPercent}%`
              }} data-id="element-1267" data-mp="zbjgpt4b0vep"></div>
              </div>

              <AnimatePresence mode="wait" data-id="element-1268" data-mp="1rs2hjg13gytc">
                <motion.div key={formStep} initial={{
                opacity: 0,
                x: 10
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -10
              }} transition={{
                duration: 0.2
              }} data-id="element-1269" data-mp="1rtx5l37nx71n">
                  {renderFormContent()}
                </motion.div>
              </AnimatePresence>
            </form>
          </div>
        </div>
      </section>

      {/* Why CCU Section */}
      <section className="bg-gray-50 py-16 border-b border-gray-200" data-id="element-1270" data-mp="1e6g8u39vbs3a">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1271" data-mp="1e89izsjowug1">
          <div className="mb-12 text-center" data-id="element-1272" data-mp="1s6r19aqtteqk">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0F3057]" data-id="element-1273" data-mp="1s3lei25o6oy0">
              Why CCU?
            </h2>
          </div>
          <div className="space-y-8 max-w-4xl mx-auto" data-id="element-1274" data-mp="33klb39z1vpxu">
            {/* Card 1 */}
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 bg-white p-6 rounded-xl shadow-sm border border-gray-100" data-id="element-1275" data-mp="2yma374sbfpi9">
              <div className="w-20 h-20 bg-[#0F3057] rounded-full flex items-center justify-center flex-shrink-0 text-white" data-id="element-1276" data-mp="186hacabzpsdf">
                <ChurchIcon className="w-10 h-10" data-id="element-1277" data-mp="1d5u99tg4ypwd" />
              </div>
              <div className="text-center sm:text-left" data-id="element-1278" data-mp="1i1j0n9i050hl">
                <h3 className="text-xl font-bold text-gray-800 mb-2" data-id="element-1279" data-mp="1m578ltajpy3d">
                  Integration of Faith and Learning
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-1280" data-mp="1r39092m1f5qg">
                  All courses are taught with a Christian worldview. This means
                  you will be equipped for success to make a positive impact in
                  the world.
                </p>
              </div>
            </div>

            {/* Card 2 */}
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 bg-white p-6 rounded-xl shadow-sm border border-gray-100" data-id="element-1281" data-mp="29u9zzsuzf5b2">
              <div className="w-20 h-20 bg-[#0F3057] rounded-full flex items-center justify-center flex-shrink-0 text-white" data-id="element-1282" data-mp="2eu4qhvauhsvl">
                <RouteIcon className="w-10 h-10" data-id="element-1283" data-mp="37awa2ns4x7d" />
              </div>
              <div className="text-center sm:text-left" data-id="element-1284" data-mp="3ubj5rabznwrv">
                <h3 className="text-xl font-bold text-gray-800 mb-2" data-id="element-1285" data-mp="3qapec6zc7zu8">
                  Flexible Online Format
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-1286" data-mp="3lco0c0grqqpm">
                  CCU's unique online and course schedule format make it
                  possible for you to juggle life, work, and family all while
                  earning your degree.
                </p>
              </div>
            </div>

            {/* Card 3 */}
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 bg-white p-6 rounded-xl shadow-sm border border-gray-100" data-id="element-1287" data-mp="d2cl0xe57x9f">
              <div className="w-20 h-20 bg-[#0F3057] rounded-full flex items-center justify-center flex-shrink-0 text-white" data-id="element-1288" data-mp="15l011yea8m3n">
                <DollarSignIcon className="w-10 h-10" data-id="element-1289" data-mp="10szzy2mh7n5h" />
              </div>
              <div className="text-center sm:text-left" data-id="element-1290" data-mp="1jpqmw4fk0tmb">
                <h3 className="text-xl font-bold text-gray-800 mb-2" data-id="element-1291" data-mp="1jscu22yj9740">
                  Affordable
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-1292" data-mp="1axygq5xkc6nj">
                  With competitive tuition rates, scholarships, and discount
                  opportunities, it's possible to earn your degree without
                  breaking the bank.
                </p>
              </div>
            </div>

            {/* Card 4 */}
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 bg-white p-6 rounded-xl shadow-sm border border-gray-100" data-id="element-1293" data-mp="15znas6ae2gal">
              <div className="w-20 h-20 bg-[#0F3057] rounded-full flex items-center justify-center flex-shrink-0 text-white" data-id="element-1294" data-mp="s172iawvohqc">
                <UsersIcon className="w-10 h-10" data-id="element-1295" data-mp="2mcc3f7gkyg1z" />
              </div>
              <div className="text-center sm:text-left" data-id="element-1296" data-mp="2mb10f9uylcbx">
                <h3 className="text-xl font-bold text-gray-800 mb-2" data-id="element-1297" data-mp="2qd5rc0u0vuuv">
                  Student Success
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-1298" data-mp="ep0uibp8zxh4">
                  CCU sets you up for success with a dedicated support staff who are invested in making sure that you are successful from the first class, to graduation, and beyond.
                </p>
              </div>
            </div>

            {/* Card 5 */}
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 bg-white p-6 rounded-xl shadow-sm border border-gray-100" data-id="element-1299" data-mp="yeugua971kdy">
              <div className="w-20 h-20 bg-[#0F3057] rounded-full flex items-center justify-center flex-shrink-0 text-white" data-id="element-1300" data-mp="tf86c8kr2i8u">
                <MedalIcon className="w-10 h-10" data-id="element-1301" data-mp="17doiwg5an2yw" />
              </div>
              <div className="text-center sm:text-left" data-id="element-1302" data-mp="12hzfg1q5l1xb">
                <h3 className="text-xl font-bold text-gray-800 mb-2" data-id="element-1303" data-mp="396vx1m967ej7">
                  Military Focused
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-1304" data-mp="34946owo6c0h4">
                  Dedicated to serving the active and veteran military, CCU has a dedicated military enrollment team with expertise in advising those who are military-affiliated. Whether you're a veteran, active duty, or spouse of either, CCU's enrollment team will ensure you maximize your benefits as you achieve your academic goals.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Programs Designed for Adult Students */}
      <section className="py-16 bg-white border-b border-gray-200" data-id="element-1305" data-mp="20fvi6t3z7zkt">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1306" data-mp="24fmd990jd1v5">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#0F3057] mb-6" data-id="element-1307" data-mp="29d5ri8x8hen3">
            Programs Designed for Adult Students
          </h2>
          <p className="text-gray-600 leading-relaxed mb-10 max-w-3xl mx-auto" data-id="element-1308" data-mp="2eb7ayuyknfz3">
            CCU's online programs are specifically tailored for adult learners who need to balance their education with work and family commitments. With flexible scheduling and a supportive community, you can achieve your academic goals without putting your life on hold.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center" data-id="element-1309" data-mp="197bpzd0fgztf">
            <div className="flex flex-col items-center" data-id="element-1310" data-mp="14eil8p4ylrhc">
              <div className="w-16 h-16 bg-[#D4A943]/10 rounded-full flex items-center justify-center mb-4" data-id="element-1311" data-mp="14cxrh1qgyckj">
                <ShieldCheckIcon className="w-8 h-8 text-[#D4A943]" data-id="element-1312" data-mp="vem6bf8dg6zh" />
              </div>
              <h3 className="text-base font-bold text-[#0F3057]" data-id="element-1313" data-mp="vc9emactai98">
                Accredited Programs
              </h3>
            </div>
            <div className="flex flex-col items-center" data-id="element-1314" data-mp="lfmujnsb7v89">
              <div className="w-16 h-16 bg-[#D4A943]/10 rounded-full flex items-center justify-center mb-4" data-id="element-1315" data-mp="li9buf505p3o">
                <CheckCircle2Icon className="w-8 h-8 text-[#D4A943]" data-id="element-1316" data-mp="2fuh0jaqqd9r0" />
              </div>
              <h3 className="text-base font-bold text-[#0F3057]" data-id="element-1317" data-mp="2fs3716bp85lk">
                100% Online
              </h3>
            </div>
            <div className="flex flex-col items-center" data-id="element-1318" data-mp="32l9ym0eahf1y">
              <div className="w-16 h-16 bg-[#D4A943]/10 rounded-full flex items-center justify-center mb-4" data-id="element-1319" data-mp="32jp42arz5w43">
                <UsersIcon className="w-8 h-8 text-[#D4A943]" data-id="element-1320" data-mp="2xkun7edkihmm" />
              </div>
              <h3 className="text-base font-bold text-[#0F3057]" data-id="element-1321" data-mp="3catnpnsli559">
                Dedicated Support
              </h3>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#0F3057] py-16" data-id="element-1322" data-mp="2jp0g90wcu2c5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1323" data-mp="2eomhk9gn84qs">
          <div className="w-12 h-1 bg-[#D4A943] mx-auto mb-6" data-id="element-1324" data-mp="2tku0z7k89250"></div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-2" data-id="element-1325" data-mp="2tj0pneyo5tqo">
            Interested in Learning More About CCU's Online{' '}
            <span className="text-[#D4A943]" data-id="element-1326" data-mp="8ud0abfqawz8">Degrees?</span>
          </h2>
          <div className="mt-8" data-id="element-1327" data-mp="8wpdkbajkj5y">
            <button onClick={scrollToForm} className="inline-block bg-white text-[#0F3057] font-bold py-3.5 px-12 rounded-full hover:shadow-lg hover:bg-gray-100 transition-all text-sm tracking-wide" data-id="element-1328" data-mp="3wkymppa7whu">
              Learn More Now
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#F5F5F5] text-gray-500 py-10 text-xs border-t border-gray-200" data-id="element-1329" data-mp="2fnvp0dtt19b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-3" data-id="element-1330" data-mp="6h952br5ixer">
          <p data-id="element-1331" data-mp="u5aa3ap4fvpf">Certain degree programs may not be available in all states.</p>
          <p className="pt-4 border-t border-gray-300 text-gray-400" data-id="element-1332" data-mp="u84x99zirumj">
            Colorado Christian University is accredited by the Higher Learning
            Commission (hlcommission.org), an institutional accreditation agency
            recognized by the U.S. Department of Education.
          </p>
          <p className="text-gray-400" data-id="element-1333" data-mp="1d4o7y3pz1ql7">
            © {new Date().getFullYear()} Colorado Christian University. All
            rights reserved.
          </p>
        </div>
      </footer>
    </div>;
}